import React, { useState, useContext, useMemo } from 'react';
import { Alert, Button, Modal, Row } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import { Column, Row as reactTableRow } from 'react-table';
import update from 'immutability-helper';
import _ from 'lodash';
import { ResponseError } from '@nintendo/forms';
import { useMsal } from '@azure/msal-react';
import { RoleUtils } from '@galaxy/lorule-react-commons';
import NinModal from '../../common/modal/NinModal';
import DataTableDraggable, { DraggableType } from '../../common/datatables/DataTableDraggable';
import ConfigContext from '../../../contexts/ConfigContext';
import { Approver } from '../../../api/actionMemoApiTypes';
import { getAmUsers } from '../../../api/amUserApi';
import { updateApprovers } from '../../../api/actionMemoApi';
import { AmUserType } from '../../../api/amUserApiTypes';
import { generateActionString } from '../tabs/Approvers';
import {
    noOptionsHandler,
    formatNameTitle,
    formatAmDisplayString,
    sendToast,
} from '../../../utils/displayUtils';
import {
    CONTRACT_SIGNER_ORIGIN,
    MANUAL_ORIGIN,
    ApproverRoutingStatusType,
} from '../../../utils/Constants';

export interface ApproversUpdateModalProps {
    title: String;
    toggleButton: JSX.Element;

    approvers: Approver[];
    amId: number;
    amNumber: number;
    supNumber: number;
    creatorId: number;
    onSubmit?: () => void;
    refresh: Function;
}

export interface userSelectOptionType {
    label: string;
    value: AmUserType;
}

const ApproversUpdateModal = (props: ApproversUpdateModalProps) => {
    const { instance } = useMsal();
    const config = useContext(ConfigContext);
    const { refresh, amId, creatorId } = props;
    const roles = RoleUtils.getRoles(instance);

    const [responseError, setResponseError] = useState('');
    const [addedUser, setAddedUser] = useState({} as userSelectOptionType);
    const [submitting, setSubmitting] = useState(false);
    const [selectionError, setSelectionError] = useState('');

    const approvers = props.approvers.map((approver) => ({
        id: approver.personId,
        approverName: approver.approverName,
        approverEmail: approver.approverEmailAddress,
        title: approver.approverTitle,
        approverId: approver.approverId,
        routingStatus: approver.routingStatus,
        stopNumber: approver.stopNumber,
        origin: approver.origin,
        draggable:
            approver.routingStatus !== ApproverRoutingStatusType.APPROVED &&
            approver.routingStatus !== ApproverRoutingStatusType.SKIPPED &&
            approver.origin !== CONTRACT_SIGNER_ORIGIN,
        deleteable:
            approver.origin === MANUAL_ORIGIN &&
            approver.routingStatus !== ApproverRoutingStatusType.APPROVED &&
            approver.routingStatus !== ApproverRoutingStatusType.SKIPPED,
    }));

    const [approversInProcess, setApproversInProcess] = useState(approvers);

    const addApprover = (user: userSelectOptionType) => {
        const maxStopNumber = approversInProcess.length;

        const noSigner =
            approversInProcess[approversInProcess.length - 1].origin !== CONTRACT_SIGNER_ORIGIN;
        const newStopNumber = noSigner ? maxStopNumber + 1 : maxStopNumber;

        const newRecord = {
            id: user.value.personId.toString(),
            approverName: user.value.preferredName,
            approverEmail: user.value.emailAddress || '',
            title: user.value.title || '',
            origin: MANUAL_ORIGIN,
            routingStatus: ApproverRoutingStatusType.NOT_APPROVED,
            stopNumber: newStopNumber,
            approverId: undefined,
            draggable: true,
            deleteable: true,
        };

        if (noSigner) {
            setApproversInProcess(update(approversInProcess, { $push: [newRecord] }));
        } else {
            const signerRecord = approversInProcess[maxStopNumber - 1];
            signerRecord.stopNumber++;

            setApproversInProcess(
                update(approversInProcess, {
                    $splice: [
                        [maxStopNumber - 1, 1],
                        [newStopNumber - 1, 0, newRecord, signerRecord],
                    ],
                }),
            );
        }
    };

    const removeApprover = (index: number) => {
        const afterShift = approversInProcess.map((item, idx) => {
            if (idx > index) {
                return { ...item, stopNumber: item.stopNumber - 1 };
            }
            return item;
        });
        setApproversInProcess(
            update(afterShift, {
                $splice: [[index, 1]],
            }),
        );
        setAddedUser({} as userSelectOptionType);
    };

    const columns = React.useMemo(
        () => [
            {
                Header: '',
                accessor: 'stopNumber',
                disableSortBy: true,
                width: 50,
            },
            {
                Header: 'Name/Title',
                accessor: 'approverName',
                Cell: (cellProps: {
                    value: string;
                    row: {
                        original: {
                            title: string;
                            origin: string;
                        };
                    };
                }) =>
                    useMemo(
                        () => (
                            <>
                                <div>{cellProps.value}</div>
                                <div className="font-italic font-weight-light">
                                    <small>{cellProps.row.original.title}</small>
                                </div>
                                <div>
                                    {cellProps.row.original.origin === CONTRACT_SIGNER_ORIGIN ? (
                                        <small>
                                            <strong className="text-danger">Contract Signer</strong>
                                        </small>
                                    ) : null}
                                </div>
                            </>
                        ),
                        [],
                    ),
                disableSortBy: true,
            },
            {
                Header: 'Status',
                accessor: 'routingStatus',
                Cell: (cellProps: { value: string }) => generateActionString(cellProps.value),
                disableSortBy: true,
            },
            {
                Header: '',
                accessor: 'deleteable',
                disableSortBy: true,
                Cell: (cellProps: { value: boolean; row: reactTableRow }) =>
                    useMemo(
                        () =>
                            cellProps.value ? (
                                <button
                                    aria-label="Remove"
                                    className="fas fa-times text-danger border-0"
                                    type="button"
                                    onClick={() => {
                                        removeApprover(cellProps.row.index);
                                    }}
                                />
                            ) : (
                                <button
                                    aria-label="Locked"
                                    type="button"
                                    className="fas fa-lock border-0"
                                    disabled
                                />
                            ),
                        [],
                    ),
            },
        ],
        [approvers],
    );

    const checkForEnable = (option: userSelectOptionType) => {
        if (_.isEmpty(option)) return false;

        if (option.value.personId === creatorId) {
            setSelectionError('AM Creator cannot be an approver');
            return false;
        }

        const filtered = approversInProcess.filter(
            (item) => Number(item.id) === option.value.personId,
        );
        if (filtered.length > 0) {
            setSelectionError('Approver already exists');
            return false;
        }

        setSelectionError('');
        return true;
    };

    const submitUpdateApprover = async () => {
        const newApprovers = approversInProcess.map((approver) => ({
            personId: approver.id,
            origin: approver.origin,
            stopNumber: approver.stopNumber,
            approverId: approver.approverId,
        }));
        try {
            await updateApprovers(
                config,
                amId,
                newApprovers,
                !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
            );
            sendToast(
                <>
                    Updated Approvers:{' '}
                    <strong>{formatAmDisplayString(props.amNumber, props.supNumber)}</strong>
                </>,
            );
            return true;
        } catch (error) {
            if (error instanceof Error) {
                setResponseError(`Update Failed:  ${error.message}`);
            }
            return false;
        }
    };

    return (
        <NinModal
            size="lg"
            button={props.toggleButton}
            render={(toggleModal) => (
                <>
                    <Modal.Header>{props.title}</Modal.Header>
                    <ResponseError errorMessage={responseError} />
                    <Modal.Body>
                        {selectionError !== '' && <Alert variant="danger">{selectionError}</Alert>}
                        <Row className="mx-2 mb-2">
                            <AsyncSelect
                                cacheOptions
                                isClearable
                                className="width-300"
                                loadOptions={async (inputValue: string) => {
                                    if (inputValue.length > 2) {
                                        const users = await getAmUsers(config, {
                                            name: inputValue,
                                        });
                                        if (users) {
                                            return users.map((user) => ({
                                                value: user,
                                                label: formatNameTitle(
                                                    user.preferredName,
                                                    user.title,
                                                ),
                                            }));
                                        }
                                    }
                                    return [];
                                }}
                                value={_.isEmpty(addedUser) ? null : addedUser}
                                onChange={(option) => {
                                    if (option) {
                                        setAddedUser(option as userSelectOptionType);
                                    } else {
                                        setAddedUser({} as userSelectOptionType);
                                    }
                                }}
                                noOptionsMessage={noOptionsHandler}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && checkForEnable(addedUser)) {
                                        addApprover(addedUser);
                                        setAddedUser({} as userSelectOptionType);
                                    }
                                }}
                            />
                            <Button
                                variant="outline-primary"
                                className="ml-auto"
                                onClick={() => {
                                    addApprover(addedUser);
                                    setAddedUser({} as userSelectOptionType);
                                }}
                                disabled={!checkForEnable(addedUser)}
                            >
                                Add Approver
                            </Button>
                        </Row>

                        <div className="react-table px-2">
                            <DataTableDraggable
                                columns={columns as Column<DraggableType>[]}
                                data={approversInProcess}
                                setData={setApproversInProcess}
                            />
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="button"
                            className="btn-outline-secondary mr-auto"
                            onClick={() => {
                                setApproversInProcess(approvers);
                                setAddedUser({} as userSelectOptionType);
                                toggleModal();
                            }}
                        >
                            Close
                        </Button>
                        <Button
                            variant="primary"
                            disabled={submitting || _.isEqual(approvers, approversInProcess)}
                            onClick={async () => {
                                setResponseError('');
                                setSubmitting(true);
                                const success = await submitUpdateApprover();

                                if (success) {
                                    refresh();
                                    toggleModal();
                                }
                                setSubmitting(false);
                            }}
                        >
                            {submitting ? <i className="fas fa-circle-notch fa-spin" /> : null} Save
                        </Button>
                    </Modal.Footer>
                </>
            )}
        />
    );
};

export default ApproversUpdateModal;
